import { CSSProperties, ImgHTMLAttributes } from 'react';
import { buildSrcset, hasVariants } from '@/lib/responsive-image';
import type { ResponsiveImageMedia } from '@/types/responsive-image';

type Props = Omit<ImgHTMLAttributes<HTMLImageElement>, 'src' | 'srcSet'> & {
    /** Media object with responsive_variants populated. Preferred. */
    media?: ResponsiveImageMedia;
    /** Fallback string src when no Media object is available. */
    src?: string;
    /** Required: caller-provided sizes attribute (layout-dependent). */
    sizes: string;
    alt: string;
};

/**
 * Renders <picture> with AVIF + WebP sources and an <img> fallback.
 *
 * If the media has no responsive_variants (or only a string `src` is given),
 * falls back to a plain <img>.
 */
export function ResponsiveImage({
    media,
    src,
    sizes,
    alt,
    style,
    className,
    loading,
    fetchPriority,
    ...rest
}: Props) {
    const fallbackSrc = media?.url ?? src;
    if (!fallbackSrc) {
        return null;
    }

    const focalStyle: CSSProperties | undefined =
        media?.focal_point && (media.focal_point.x != null && media.focal_point.y != null)
            ? { objectPosition: `${media.focal_point.x * 100}% ${media.focal_point.y * 100}%` }
            : undefined;

    const mergedStyle = focalStyle ? { ...focalStyle, ...style } : style;

    if (!media || !hasVariants(media)) {
        return (
            <img
                src={fallbackSrc}
                alt={alt}
                sizes={sizes}
                style={mergedStyle}
                className={className}
                loading={loading}
                fetchPriority={fetchPriority}
                {...rest}
            />
        );
    }

    const avifSrcset = buildSrcset(media.responsive_variants?.avif);
    const webpSrcset = buildSrcset(media.responsive_variants?.webp);

    return (
        <picture>
            {avifSrcset && <source type="image/avif" srcSet={avifSrcset} sizes={sizes} />}
            {webpSrcset && <source type="image/webp" srcSet={webpSrcset} sizes={sizes} />}
            <img
                src={fallbackSrc}
                alt={alt}
                sizes={sizes}
                style={mergedStyle}
                className={className}
                loading={loading}
                fetchPriority={fetchPriority}
                {...rest}
            />
        </picture>
    );
}

export default ResponsiveImage;
